import { getChartLabel } from "./chartLabel";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Form, Nav } from "react-bootstrap";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

const MonCharts = ({ data, filters, selectedState }) => {
  const [chartDataByService, setChartDataByService] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");

  const descriptions = [
    "DACO",
    "DPM/CPM",
    "DIS/CSO",
    "DA-M&E",
    "DA-Accounts",
    "DA-Programme",
    "DMDO",
  ];

  const services = [
    "total_visits",
    "Total_TI",
    "ti",
    "Total_LWS",
    "lws",
    "Total_One_Stop_Centre",
    "one_stop_centre",
    "Total_Prisons",
    "prisons",
    "Total_OCS",
    "ocs",
    "Total_OST_center",
    "ost_center",
    "Total_ICTC",
    "ictc",
    "Total_SSK",
    "ssk",
    "Total_DSRC",
    "dsrc",
    "Total_ART_Centre",
    "art_centre",
    "Total_Link_ART_Centre",
    "link_art_centre",
    "Total_VL_lab",
    "vl_lab",
    "CSC",
    "SRL",
  ];

  const formatServiceName = (service) => {
    return service
      .replace(/_/g, " ") // replace underscores with space
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize first letter of each word
  };

  const serviceTitles = {
    total_visits: "Total Visits",
    Total_TI: "Number of TI Facilities",
    ti: "Number of TI Visited",
    Total_LWS: "Number of LWS Facilities",
    lws: "Number of LWS Visited",
    Total_One_Stop_Centre: "Number of One Stop Centre Facilities",
    one_stop_centre: "Number of One Stop Centre Visited",
    Total_Prisons: "Number of Prisons Facilities",
    prisons: "Number of Prisons Visited",
    Total_OCS: "Number of OCS Facilities",
    ocs: "Number of OCS Visited",
    Total_OST_center: "Number of OST center Facilities",
    ost_center: "Number of OST center Visited",
    Total_ICTC: "Number of ICTC Facilities",
    ictc: "Number of ICTC Visited",
    Total_SSK: "Number of SSK Facilities",
    ssk: "Number of SSK Visited",
    Total_DSRC: "Number of DSRC Facilities",
    dsrc: "Number of DSRC Visited",
    Total_ART_Centre: "Number of ART Centre Facilities",
    art_centre: "Number of ART Centre Visited",
    Total_Link_ART_Centre: "Number of Link Art Centre Facilities",
    link_art_centre: "Number of Link Art Centre Visited",
    Total_VL_lab: "Number of VL Lab Facilities",
    vl_lab: "Number of VL Lab Visited",
    CSC: "CSC",
    SRL: "SRL",
  };

  const places = Array.from(
    new Set(
      Object.values(chartDataByService)
        .flat()
        .map((entry) => entry.Place),
    ),
  );

  const descriptionTitles = {
    DACO: "DACO",
    "DPM/CPM": "DPM/CPM",
    "DIS/CSO": "DIS/CSO",
    "DA-M&E": "DA-M&E",
    "DA-Accounts": "DA-Accounts",
    "DA-Programme": "DA-Programme",
    DMDO: "DMDO",
  };

  const descriptionKeys = Object.keys(descriptionTitles);

  useEffect(() => {
    const organizedData = {};

    services.forEach((service) => {
      const chartData = [];

      if (!Array.isArray(data)) {
        console.warn("Invalid data format, expected an array");
        return;
      }

      const places = [...new Set(data.map((entry) => entry.Place))];

      places.forEach((place) => {
        const entry = { Place: place };

        descriptions.forEach((desc) => {
          const item = data.find((row) => {
            if (
              !row ||
              typeof row.Place !== "string" ||
              typeof row.Description !== "string"
            ) {
              console.warn("Skipping row due to missing data:", row);
              return false;
            }

            return (
              row.Place.trim().toLowerCase() === place.trim().toLowerCase() &&
              row.Description.trim().toLowerCase() === desc.trim().toLowerCase()
            );
          });

          const value = item && item[service];
          entry[desc] = value && !isNaN(value) ? parseFloat(value) : 0;
        });

        chartData.push(entry);
      });

      organizedData[service] = chartData;
    });

    setChartDataByService(organizedData);
    if (!selectedDescription && descriptions.length > 0) {
      setSelectedDescription(descriptions[0]);
    }
  }, [data]);

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );
  }

  const chunkArray = (array, chunkCount) => {
    const result = [];
    const chunkSize = Math.ceil(array.length / chunkCount);

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  };

  const getChartCount = (length) => {
    if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  return (
    <div>
      {/* Toggle switch at top right */}
      <div className="card hptCard mt-4 border-0">
        <div class="card-header brBtomlr">
          <div class="headnumText justify-content-between">
            <div class="cardTitle d-flex align-items-center gap-3">
              <h5>
                Monitoring Visit Data {getChartLabel(filters, selectedState)}
              </h5>
            </div>
            <div class="headnumText justify-content-between">
              <div className="custom-tabs-wrapper">
                <Nav variant="tabs" className="custom-tabs">
                  {descriptions.map((desc) => (
                    <Nav.Item key={desc}>
                      <Nav.Link
                        active={selectedDescription === desc}
                        onClick={() => setSelectedDescription(desc)}
                      >
                        {descriptionTitles[desc]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </div>
              <div class="cardDropDown gap-3">
                <div style={{ marginRight: "10px", fontWeight: 500 }}>
                  {showTable ? "Chart View" : "Table View"}
                </div>
                <Form>
                  <Form.Check
                    type="switch"
                    id="chart-table-toggle"
                    onChange={() => setShowTable((prev) => !prev)}
                    checked={showTable}
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showTable ? (
        <div>
          <>
            <div className="card hptCard mt-4 border-0">
              <div className="card-body">
                <h6 style={{ fontWeight: "bold" }}>
                  {descriptionTitles[selectedDescription]}
                </h6>
              </div>

              <div className="table-responsive table-scroll">
                <table className="table table-bordered ">
                  <thead className="thead-light">
                    <tr>
                      <th className="fw-bold">Place</th>

                      {services.map((service) => (
                        <th key={service} className="fw-bold text-center">
                          {/* {formatServiceName(service).toUpperCase()} */}
                          {(
                            serviceTitles[service] || formatServiceName(service)
                          ).toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {places.map((place, rowIdx) => (
                      <tr key={rowIdx}>
                        <td style={{ fontWeight: "bold" }}>{place}</td>
                        {services.map((service, colIdx) => {
                          const entry = chartDataByService[service]?.find(
                            (row) => row.Place === place,
                          );
                          return (
                            <td key={colIdx}>
                              {entry ? entry[selectedDescription] : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: "50px" }}>
            {/* <h5 style={{ fontWeight: "bold" }}>
              {descriptionTitles[selectedDescription]}
            </h5> */}

            {(() => {
              // Filter services that have data for the selected description
              const servicesForDesc = services.filter((service) =>
                chartDataByService[service]?.some(
                  (row) => row[selectedDescription] !== undefined,
                ),
              );

              // Separate "total_visits" from other services
              const totalVisitsService = servicesForDesc.find(
                (s) => s.toLowerCase() === "total_visits",
              );
              const otherServices = servicesForDesc.filter(
                (s) => s.toLowerCase() !== "total_visits",
              );

              // Function to prepare chart data for a list of services
              const getChartData = (serviceList, placeChunk) =>
                placeChunk.map((place) => {
                  let rowData = { Place: place };

                  serviceList.forEach((service) => {
                    const entry = chartDataByService[service]?.find(
                      (row) => row.Place === place,
                    );

                    rowData[service] = entry
                      ? parseFloat(entry[selectedDescription]) || 0
                      : 0;
                  });

                  return rowData;
                });

              const chartCount = getChartCount(places.length);
              const chunkedPlaces = chunkArray(places, chartCount);

              // Chart for total_visits (if exists)
              const totalVisitsChart =
                totalVisitsService &&
                chunkedPlaces.map((placeChunk, idx) => (
                  <div className="card hptCard mt-4 border-0">
                    <div class="card-body">
                      <h6 style={{ fontWeight: "bold" }}>
                        {descriptionTitles[selectedDescription]} - Total Visits
                      </h6>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart
                        data={getChartData([totalVisitsService], placeChunk)}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 130,
                        }}
                      >
                        <XAxis
                          dataKey="Place"
                          angle={-90}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip
                          content={<CustomTooltip />}
                          formatter={(value, name) => [
                            value,
                            serviceTitles[name] || name,
                          ]}
                        />
                        <Legend
                          verticalAlign="top"
                          content={<CustomLegend />}
                        />
                        {/* <Tooltip
                        contentStyle={{
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                        itemStyle={{ color: "#333", fontWeight: "bold" }}
                        cursor={{ fill: "none" }}
                      />
                      <Legend verticalAlign="top" align="center" /> */}
                        <Bar
                          dataKey={totalVisitsService}
                          name={serviceTitles[totalVisitsService]}
                          fill="#dd1e47"
                        >
                          <LabelList
                            dataKey={totalVisitsService}
                            position="top"
                            content={({ x, y, width, value }) => {
                              if (value == null) return null;
                              return (
                                <text
                                  x={x + width / 2} // center horizontally
                                  y={y - 5} // position slightly above bar
                                  fontSize={12}
                                  fontWeight="bold"
                                  textAnchor="start" // 👈 could also try "middle" for alignment
                                  transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                                >
                                  {value}
                                </text>
                              );
                            }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ));

              // Group other services into chunks of 4
              const chunkSize = 2;
              const otherChunks = [];
              for (let i = 0; i < otherServices.length; i += chunkSize) {
                otherChunks.push(otherServices.slice(i, i + chunkSize));
              }

              const otherCharts = otherChunks.flatMap((chunk, idx) =>
                chunkedPlaces.map((placeChunk, pIdx) => (
                  <div
                    key={`${idx}-${pIdx}`}
                    className="card hptCard mt-4 border-0"
                  >
                    {/* <div class="card-body">
                    <h6 style={{ fontWeight: "bold" }}></h6>
                    
                  </div> */}
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart
                        data={getChartData(chunk, placeChunk)}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 130,
                        }}
                      >
                        <XAxis
                          dataKey="Place"
                          angle={-90}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip
                          content={<CustomTooltip />}
                          formatter={(value, name) => [
                            value,
                            serviceTitles[name] || name,
                          ]}
                        />
                        <Legend
                          verticalAlign="top"
                          content={<CustomLegend />}
                        />
                        {/* <Tooltip
                        contentStyle={{
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                        itemStyle={{ color: "#333", fontWeight: "bold" }}
                        cursor={{ fill: "none" }}
                      />
                      <Legend verticalAlign="top" align="center" /> */}
                        {chunk.map((service, i) => (
                          <Bar
                            key={service}
                            dataKey={service}
                            name={serviceTitles[service]}
                            fill={["#00a084", "#ffc40c"][i % 2]}
                          >
                            <LabelList
                              dataKey={service}
                              position="top"
                              content={({ x, y, width, value }) => {
                                if (value == null) return null;
                                return (
                                  <text
                                    x={x + width / 2} // center horizontally
                                    y={y - 5} // position slightly above bar
                                    fontSize={12}
                                    fontWeight="bold"
                                    textAnchor="start" // 👈 could also try "middle" for alignment
                                    transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                                  >
                                    {value}
                                  </text>
                                );
                              }}
                            />
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )),
              );

              return (
                <>
                  {totalVisitsChart}
                  {otherCharts}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonCharts;
